import { useState, useEffect, useMemo } from 'react';
import { useSMPNavigate } from './hooks/useSMPNavigate';
import { Card, CardContent } from '@design-system/Card';
import { Button } from '@design-system/Button';
import { NavBar } from '@design-system/NavBar';
import { Input } from '@design-system/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/design-system/Select';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@design-system/Dialog';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { Skeleton } from '@design-system/Skeleton';
import { useSMPData } from './hooks/useSMPData';
import { useMaintenanceGuard } from './hooks/useMaintenanceGuard';
import { useUIMessages } from './hooks/useUIMessages';
import { useUpdateShippingAddress } from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import {
  useCountriesQuery,
  useStatesQuery,
  useAddressValidationMutation,
} from '@viome/viome-base/src/usecase/Register/RegisterQuery';
import {
  SMP_PLATFORM,
  LOOP_ADDRESS_ID,
} from '@viome/viome-base/src/usecase/SMP/SMP.model';
import type {
  SMPBoldAddress,
  UpdateAddressRequest,
} from '@viome/viome-base/src/usecase/SMP/SMP.model';
import type { Address, InvalidFields } from '@viome/viome-base/src/usecase/Register/Register.model';
import { formatShippingAddress, isSubscriptionActiveState } from './utils/smpHelpers';
import { OIPCard, SMPDialogContent, SMPMutationErrorDialog } from './components';
import { logSMPMutationError } from '@/utils/sentryLogger';
import { InfoSheet } from '@/components/screens/HealthPages/InfoSheet';
import { INFO_SHEET_CONTENT } from '@/utils/InfoSheet/InfoSheetContent';

type PageMode = 'view' | 'disclaimer' | 'edit';

interface AddressFormData {
  firstName: string;
  lastName: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  zip: string;
  stateCode: string;
  stateName: string;
  countryCode: string;
  countryName: string;
  phone: string;
}

const REQUIRED_FIELDS: (keyof AddressFormData)[] = [
  'firstName',
  'lastName',
  'addressLine1',
  'city',
  'zip',
  'stateCode',
  'countryCode',
  'phone',
];

const FORM_TO_API_FIELD: Partial<Record<keyof AddressFormData, string>> = {
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  city: 'city',
  zip: 'zip',
  stateCode: 'state',
};

const getFieldError = (fieldErrors: InvalidFields | null, formField: keyof AddressFormData): string | null => {
  if (!fieldErrors) return null;
  const apiField = FORM_TO_API_FIELD[formField];
  if (!apiField) return null;
  return (fieldErrors as Record<string, string | null | undefined>)[apiField] ?? null;
};

const FieldError = ({ fieldErrors, field }: { fieldErrors: InvalidFields | null; field: keyof AddressFormData }) => {
  const message = getFieldError(fieldErrors, field);
  if (!message) return null;
  return <p className="typography-caption2 text-error -mt-2 ml-1">{message}</p>;
};

const initFormData = (address?: SMPBoldAddress): AddressFormData => ({
  firstName: address?.first_name || '',
  lastName: address?.last_name || '',
  company: address?.company || '',
  addressLine1: address?.street1 || '',
  addressLine2: address?.street2 || '',
  city: address?.city || '',
  zip: address?.zip || '',
  stateCode: address?.province_code || '',
  stateName: address?.province || '',
  countryCode: address?.country_code || 'US',
  countryName: address?.country || 'United States',
  phone: address?.phone || '',
});

const isFormValid = (form: AddressFormData): boolean =>
  REQUIRED_FIELDS.every((field) => form[field]?.trim());

// ============================================
// Address Suggestion Dialog
// ============================================

const AddressSuggestionDialog = ({
  open,
  userAddress,
  suggestedAddress,
  onUseSuggestion,
  onKeepEdit,
  onEditAddress,
  onClose,
}: {
  open: boolean;
  userAddress: AddressFormData;
  suggestedAddress: Address | null;
  onUseSuggestion: () => void;
  onKeepEdit: () => void;
  onEditAddress: () => void;
  onClose: () => void;
}) => {
  const [selected, setSelected] = useState<'suggestion' | 'user'>(
    suggestedAddress ? 'suggestion' : 'user',
  );

  const userLines = [
    `${userAddress.addressLine1}${userAddress.addressLine2 ? `, ${userAddress.addressLine2}` : ''}`,
    `${userAddress.city}, ${userAddress.stateCode} ${userAddress.zip}`,
  ];

  const suggestedLines = suggestedAddress
    ? [
        `${suggestedAddress.addressLine1}${suggestedAddress.addressLine2 ? `, ${suggestedAddress.addressLine2}` : ''}`,
        `${suggestedAddress.city}, ${suggestedAddress.state} ${suggestedAddress.zip}`,
      ]
    : [];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <SMPDialogContent>
        <DialogHeader className="gap-6 text-start">
          <DialogTitle className="typography-h3 text-primary">
            Confirm your address
          </DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-col gap-4">
              <button
                type="button"
                onClick={() => setSelected('user')}
                className={`flex items-start gap-3 rounded-xl p-4 text-left border transition-colors ${
                  selected === 'user'
                    ? 'border-foreground bg-overlay-primary'
                    : 'border-stroke-secondary-subtle'
                }`}
              >
                <div
                  className={`mt-1 size-4 rounded-full border-2 shrink-0 ${
                    selected === 'user'
                      ? 'border-foreground bg-foreground'
                      : 'border-stroke-secondary-subtle'
                  }`}
                />
                <div className="flex flex-col gap-1">
                  <span className="typography-body text-primary">Your edit</span>
                  <div className="flex flex-col fs-mask">
                    {userLines.map((line) => (
                      <span key={line} className="typography-caption1 text-tertiary">
                        {line}
                      </span>
                    ))}
                  </div>
                </div>
              </button>

              {suggestedAddress && (
                <button
                  type="button"
                  onClick={() => setSelected('suggestion')}
                  className={`flex items-start gap-3 rounded-xl p-4 text-left border transition-colors ${
                    selected === 'suggestion'
                      ? 'border-foreground bg-overlay-primary'
                      : 'border-stroke-secondary-subtle'
                  }`}
                >
                  <div
                    className={`mt-1 size-4 rounded-full border-2 shrink-0 ${
                      selected === 'suggestion'
                        ? 'border-foreground bg-foreground'
                        : 'border-stroke-secondary-subtle'
                    }`}
                  />
                  <div className="flex flex-col gap-1">
                    <span className="typography-body text-primary">Suggestion</span>
                    <div className="flex flex-col fs-mask">
                      {suggestedLines.map((line) => (
                        <span key={line} className="typography-caption1 text-tertiary">
                          {line}
                        </span>
                      ))}
                    </div>
                  </div>
                </button>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Button
            variant="secondary"
            onClick={onEditAddress}
          >
            Edit Address
          </Button>
          <Button
            onClick={selected === 'suggestion' ? onUseSuggestion : onKeepEdit}
          >
            {selected === 'suggestion' ? 'Use Suggestion' : 'Keep my Edit'}
          </Button>
        </div>
      </SMPDialogContent>
    </Dialog>
  );
};

// ============================================
// Main Page
// ============================================

export const SMPChangeAddressPage = () => {
  const { smpGoBack } = useSMPNavigate();
  const {
    subscriptionId,
    subscriptionDetail,
    platform,
    queryParams,
    showMaintenance,
    hasOrderInProgress,
    uiMessages,
    isLoading: isSMPLoading,
    isError: isSMPError,
  } = useSMPData({
    skip: { discounts: true, changePlan: true, canSkip: true, holdingPattern: true },
  });

  useMaintenanceGuard(showMaintenance);

  const { getMessageText } = useUIMessages(uiMessages);

  const [mode, setMode] = useState<PageMode>('view');
  const [formData, setFormData] = useState<AddressFormData>(() =>
    initFormData(subscriptionDetail?.shipping_address),
  );
  const [selectedCountry, setSelectedCountry] = useState<string>(
    subscriptionDetail?.shipping_address?.country_code || 'US',
  );
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<InvalidFields | null>(null);

  const { data: countriesData, isLoading: isLoadingCountries } = useCountriesQuery();
  const { data: statesData } = useStatesQuery(selectedCountry);

  const updateAddressMutation = useUpdateShippingAddress();
  const addressValidationMutation = useAddressValidationMutation();

  useEffect(() => {
    if (subscriptionDetail?.shipping_address && formData.firstName === '' && formData.addressLine1 === '') {
      const newFormData = initFormData(subscriptionDetail.shipping_address);
      setFormData(newFormData);
      setSelectedCountry(newFormData.countryCode);
    }
  }, [subscriptionDetail]);

  const countryOptions = useMemo(
    () =>
      countriesData?.countries
        ?.filter((c) => c.isEnabled)
        ?.map((c) => ({ value: c.countryCode, label: c.name, phoneCode: c.phoneCode })) || [],
    [countriesData],
  );

  const stateOptions = useMemo(
    () =>
      statesData?.countrySubdivisions?.map((s) => ({
        value: s.code,
        label: s.name,
      })) || [],
    [statesData],
  );

  const phoneCode = useMemo(() => {
    const country = countryOptions.find((c) => c.value === selectedCountry);
    return country?.phoneCode || '+1';
  }, [countryOptions, selectedCountry]);

  const goBack = () => smpGoBack();

  if (isSMPLoading || isLoadingCountries) {
    return (
      <section className="page-container flex flex-col gap-12 pb-24">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={goBack} />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </section>
    );
  }

  if (isSMPError || !subscriptionDetail) {
    return (
      <ErrorLoadingPage variant="light" errorType="error" hideBackground hideNavBar />
    );
  }

  const shippingAddress = subscriptionDetail.shipping_address;
  const isActive = isSubscriptionActiveState(subscriptionDetail.subscription_status);

  const addressLines = shippingAddress ? formatShippingAddress(shippingAddress) : [];
  const hasAddress = addressLines.length > 0;

  const handleChangeField = (field: keyof AddressFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setValidationError(null);
    setFieldErrors(null);
  };

  const handleCountryChange = (countryCode: string) => {
    const country = countryOptions.find((c) => c.value === countryCode);
    setSelectedCountry(countryCode);
    setFormData((prev) => ({
      ...prev,
      countryCode,
      countryName: country?.label || countryCode,
      stateCode: '',
      stateName: '',
    }));
    setValidationError(null);
    setFieldErrors(null);
  };

  const handleStateChange = (stateCode: string) => {
    const state = stateOptions.find((s) => s.value === stateCode);
    setFormData((prev) => ({
      ...prev,
      stateCode,
      stateName: state?.label || stateCode,
    }));
    setValidationError(null);
    setFieldErrors(null);
  };

  const handleEditClick = () => {
    if (hasOrderInProgress) {
      setMode('disclaimer');
    } else {
      setMode('edit');
    }
  };

  const isBoldV2 = platform === SMP_PLATFORM.BOLD_V2;

  const buildUpdatePayload = (data: AddressFormData): UpdateAddressRequest => ({
    customerId: queryParams.customerId,
    addressId: isBoldV2 ? (shippingAddress?.id ?? 0) : LOOP_ADDRESS_ID,
    subscriptionId,
    address: {
      is_default: true,
      first_name: data.firstName.trim(),
      last_name: data.lastName.trim(),
      street1: data.addressLine1.trim(),
      street2: data.addressLine2.trim() || undefined,
      city: data.city.trim(),
      province: data.stateCode,
      province_code: data.stateCode,
      country: data.countryCode,
      country_code: data.countryCode,
      zip: data.zip.trim(),
      phone: data.phone.trim() || undefined,
    },
  });

  const submitUpdate = (data: AddressFormData) => {
    const payload = buildUpdatePayload(data);
    updateAddressMutation.mutate(payload, {
      onSuccess: () => setShowSuccessDialog(true),
      onError: (error) => {
        setShowErrorDialog(true);
        logSMPMutationError('changeAddress', error, {
          screen: 'SMPChangeAddressPage',
          subscriptionId,
        });
      },
    });
  };

  const handleSave = () => {
    if (!isFormValid(formData)) return;

    const validationPayload: Address = {
      country: formData.countryCode,
      addressLine1: formData.addressLine1.trim(),
      addressLine2: formData.addressLine2.trim() || null,
      zip: formData.zip.trim(),
      city: formData.city.trim(),
      state: formData.stateCode,
    };

    addressValidationMutation.mutate(validationPayload, {
      onSuccess: (result) => {
        if (result.shippable && !result.formattedAddress) {
          submitUpdate(formData);
          return;
        }

        if (result.formattedAddress || result.address) {
          setShowSuggestionDialog(true);
          return;
        }

        if (!result.shippable) {
          setValidationError(result.message || 'Address cannot be shipped to. Please check and try again.');
          if (result.invalidFields) {
            setFieldErrors(result.invalidFields);
          }
        }
      },
      onError: (error) => {
        setValidationError(error.message || 'Failed to validate address. Please try again.');
        logSMPMutationError(
          'validateAddress',
          error,
          { screen: 'SMPChangeAddressPage', subscriptionId },
          'warn',
        );
      },
    });
  };

  const handleUseSuggestion = () => {
    const suggested = addressValidationMutation.data?.address;
    if (!suggested) return;

    const updatedForm: AddressFormData = {
      ...formData,
      addressLine1: suggested.addressLine1,
      addressLine2: suggested.addressLine2 || '',
      city: suggested.city,
      stateCode: suggested.state,
      zip: suggested.zip,
    };
    setFormData(updatedForm);
    setShowSuggestionDialog(false);
    submitUpdate(updatedForm);
  };

  const handleKeepEdit = () => {
    setShowSuggestionDialog(false);
    submitUpdate(formData);
  };

  const handleEditFromSuggestion = () => {
    setShowSuggestionDialog(false);
  };

  const isSubmitting = updateAddressMutation.isPending || addressValidationMutation.isPending;

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="smp-changeAddressPage"
    >
      <div className="flex flex-col gap-6">
        <NavBar
          returnButtonTitle="Back"
          className="md:-ml-9"
          onReturnClick={goBack}
        />
        <h1 className="typography-h6 text-primary" data-testid="smp-changeAddress-title">
          Shipping Information
        </h1>
      </div>

      {/* VIEW MODE */}
      {mode === 'view' && (
        <>
          <Card data-testid="smp-changeAddress-viewCard">
            <CardContent className="flex flex-col gap-3 py-5 px-5">
              <span className="typography-body text-primary">Shipping Address</span>
              {hasAddress ? (
                <div className="flex flex-col typography-body text-tertiary fs-mask">
                  {addressLines.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </div>
              ) : (
                <span className="typography-body text-tertiary">
                  No shipping address on file.
                </span>
              )}
            </CardContent>
          </Card>

          {isActive && (
            <Button
              onClick={handleEditClick}
              className="self-start"
              data-testid="smp-changeAddress-editBtn"
            >
              {hasAddress ? 'Change address' : 'Add address'}
            </Button>
          )}
        </>
      )}

      {/* DISCLAIMER MODE (order in progress) */}
      {mode === 'disclaimer' && subscriptionDetail.lacore_order && (
        <>
          <OIPCard
            lacoreOrder={subscriptionDetail.lacore_order}
            planName={subscriptionDetail.line_items?.[0]?.product_name}
            productImage={subscriptionDetail.line_items?.[0]?.image}
          />
          <p className="typography-body text-primary max-w-xl">
            {getMessageText('smp_shipping_address_order_in_progress_disclaimer', 'headerText')}
          </p>
          <p className="typography-body text-primary max-w-xl">
            {getMessageText('smp_shipping_address_order_in_progress_disclaimer', 'bodyText')}
          </p>
          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Button
              onClick={() => setMode('edit')}
              data-testid="smp-changeAddress-disclaimerContinue"
            >
              Yes
            </Button>
            <Button
              variant="secondary"
              onClick={goBack}
              data-testid="smp-changeAddress-disclaimerCancel"
            >
              Cancel
            </Button>
          </div>
        </>
      )}

      {/* EDIT MODE */}
      {mode === 'edit' && (
        <>
          <p className="typography-body text-secondary max-w-xl">
            Changing address will only apply to future orders and will not update
            the shipping address of an order that has already been charged.
          </p>

          <div className="flex flex-col gap-4 w-full max-w-sm">
            <Input
              placeholder="First Name"
              value={formData.firstName}
              onChange={(e) => handleChangeField('firstName', e.target.value)}
              data-testid="smp-changeAddress-firstName"
            />
            <Input
              placeholder="Last Name"
              value={formData.lastName}
              onChange={(e) => handleChangeField('lastName', e.target.value)}
              data-testid="smp-changeAddress-lastName"
            />
            <Input
              placeholder="Company (Optional)"
              value={formData.company}
              onChange={(e) => handleChangeField('company', e.target.value)}
              data-testid="smp-changeAddress-company"
            />
            <Input
              placeholder="Street Number"
              value={formData.addressLine1}
              onChange={(e) => handleChangeField('addressLine1', e.target.value)}
              data-testid="smp-changeAddress-street"
            />
            <FieldError fieldErrors={fieldErrors} field="addressLine1" />
            <Input
              placeholder="Apartment/Suite No. (Optional)"
              value={formData.addressLine2}
              onChange={(e) => handleChangeField('addressLine2', e.target.value)}
              data-testid="smp-changeAddress-apt"
            />
            <FieldError fieldErrors={fieldErrors} field="addressLine2" />
            <Input
              placeholder="City"
              value={formData.city}
              onChange={(e) => handleChangeField('city', e.target.value)}
              data-testid="smp-changeAddress-city"
            />
            <FieldError fieldErrors={fieldErrors} field="city" />
            <Input
              placeholder="Zip Code"
              value={formData.zip}
              onChange={(e) => handleChangeField('zip', e.target.value)}
              data-testid="smp-changeAddress-zip"
            />
            <FieldError fieldErrors={fieldErrors} field="zip" />

            <Select
              value={formData.stateCode}
              onValueChange={handleStateChange}
            >
              <SelectTrigger data-testid="smp-changeAddress-state">
                <SelectValue placeholder="State/Province" />
              </SelectTrigger>
              <SelectContent>
                {stateOptions.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError fieldErrors={fieldErrors} field="stateCode" />
            {stateOptions.length === 0 && (
              <p className="typography-caption2 text-secondary ml-2 -mt-2">
                {`Why can't I select my state or province?`}
                <InfoSheet
                  mode="light"
                  title={INFO_SHEET_CONTENT.registrationProvince.title}
                  description={INFO_SHEET_CONTENT.registrationProvince.description}
                />
              </p>
            )}

            <Select
              value={formData.countryCode}
              onValueChange={handleCountryChange}
            >
              <SelectTrigger data-testid="smp-changeAddress-country">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                {countryOptions.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-2">
              <div className="w-24 shrink-0">
                <Input
                  value={`${phoneCode} (${formData.countryCode})`}
                  readOnly
                  className="text-center"
                  data-testid="smp-changeAddress-phoneCode"
                />
              </div>
              <Input
                placeholder="Phone Number"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChangeField('phone', e.target.value)}
                data-testid="smp-changeAddress-phone"
              />
            </div>

            {validationError && (
              <p className="typography-caption1 text-error">{validationError}</p>
            )}

            <Button
              onClick={handleSave}
              disabled={!isFormValid(formData) || isSubmitting}
              className="mt-4"
              data-testid="smp-changeAddress-save"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </>
      )}

      {/* Address Suggestion Dialog */}
      <AddressSuggestionDialog
        open={showSuggestionDialog}
        userAddress={formData}
        suggestedAddress={addressValidationMutation.data?.address ?? null}
        onUseSuggestion={handleUseSuggestion}
        onKeepEdit={handleKeepEdit}
        onEditAddress={handleEditFromSuggestion}
        onClose={handleEditFromSuggestion}
      />

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={(isOpen) => { if (!isOpen) goBack(); }}>
        <SMPDialogContent>
          <DialogHeader className="gap-6 text-start">
            <DialogTitle className="typography-h3 text-primary">
              Address Updated
            </DialogTitle>
            <DialogDescription className="typography-body text-secondary">
              You have successfully updated your shipping address.
            </DialogDescription>
          </DialogHeader>
          <Button
            onClick={goBack}
            data-testid="smp-changeAddress-successOk"
          >
            OK
          </Button>
        </SMPDialogContent>
      </Dialog>

      {/* Error Dialog */}
      <SMPMutationErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        description="Something went wrong while updating your shipping address. Please try again."
      />
    </section>
  );
};
